import React, { useState, useEffect, useRef } from 'react';
import {
  FormControl,
  Checkbox,
  ListItemText,
  Typography,
  Dialog,
  DialogContent,
  Button,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Slide from '@mui/material/Slide';
import { isMobileOnly } from 'react-device-detect';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MuiMultiSelectListBottomSheet = ({
  label,
  options,
  onChange,
  value,
  defaultValue = [],
  widthValue,
  fullWidth = false,
  customSx = {},
  required = false,
  errorMessage,
  customStyle = null,
  disabled = false, // This prop controls the disabled state
  subTitle = "",
  disableHelperText = false
}) => {
  const [internalSelectedValue, setInternalSelectedValue] = useState(
    Array.isArray(value) ? value : defaultValue
  );
  const [open, setOpen] = useState(false);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState(false);
  const dialogContentRef = useRef(null);

  useEffect(() => {
    const valueAsArray = Array.isArray(value) ? value : [];
    if (!open && JSON.stringify(valueAsArray) !== JSON.stringify(internalSelectedValue)) {
      setInternalSelectedValue(valueAsArray);
    }
    if (required) {
      setError(!valueAsArray || valueAsArray.length === 0);
    }
  }, [value, open, required]);

  const handleToggleOption = (optionValue) => {
    setInternalSelectedValue((prev) =>
      prev.includes(optionValue)
        ? prev.filter((val) => val !== optionValue)
        : [...prev, optionValue]
    );
  };

  const handleOpen = () => {
    if (!disabled) { // Only open if not disabled
      setOpen(true);
      setInternalSelectedValue(Array.isArray(value) ? value : []);
    }
  };

  const handleApply = () => {
    if (typeof onChange === 'function') {
      const validSelectedValueArray = internalSelectedValue.filter(val =>
        options.some(option => option.value === val)
      );
      onChange(validSelectedValueArray); // Apply even if it's empty
    }
    setOpen(false);
    setTouched(true);
    setError(required && internalSelectedValue.length === 0);
  };

  const handleClose = () => {
    if (typeof onChange === 'function') {
      onChange(internalSelectedValue.length > 0 ? internalSelectedValue : []);
    }
    setOpen(false);
    setTouched(true);
    setError(required && internalSelectedValue.length === 0);
  };

  const selectedLabels = (Array.isArray(value) ? value : []).map(val => {
    const match = options.find(opt => opt.value === val);
    return match ? match.label : val;
  });

  const textFieldDisplayValue = selectedLabels.length > 0 ? selectedLabels.join(", ") : "";
  const textFieldLabel = selectedLabels.length === 0 ? label || "" : "";

  const minWidthCh = 10;
  const maxWidthCh = 40;
  const computedWidthCh = Math.min(Math.max(textFieldDisplayValue.length + 3, minWidthCh), maxWidthCh);
  const dynamicWidth = `${computedWidthCh}ch`;

  // Define the common color for the arrow icon and list item text when enabled
  const enabledIconAndTextColor = '#191C1C';
  // Define the disabled color
  const disabledIconColor = 'rgba(0, 0, 0, 0.38)'; // Standard disabled text/icon color in MUI

  return (
    <FormControl
      sx={{
        width: fullWidth ? '100%' : (isMobileOnly ? (widthValue ? '100%' : dynamicWidth) : dynamicWidth),
        paddingRight: isMobileOnly && widthValue ? '20px' : '0px',
        ...customSx,
        ...(customStyle || {}),
      }}
      required={required}
      error={error}
      variant="outlined"
      disabled={disabled} // This disables the FormControl
    >
      <TextField
        label={textFieldLabel}
        value={textFieldDisplayValue}
        onClick={handleOpen}
        fullWidth={fullWidth}
        error={error}
        helperText={!disableHelperText && (error ? errorMessage || "Dette feltet er påkrevd" : " ")}
        required={required}
        disabled={disabled} // This disables the TextField
        InputLabelProps={{ shrink: selectedLabels.length > 0 || !!textFieldLabel }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <ArrowDropDownIcon
              sx={{
                marginRight: '6px',
                // Apply disabled color if `disabled` prop is true, otherwise use the enabled color
                color: disabled ? disabledIconColor : enabledIconAndTextColor,
              }}
            />
          ),
          style: { cursor: disabled ? "not-allowed" : "pointer" },
        }}
        sx={{
          width: fullWidth ? '100%' : dynamicWidth,
          "& .MuiOutlinedInput-root": {
            height: '35px',
            borderRadius: '8px',
            padding: '0 8px',
            '& fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.23)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.5) !important',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00696D !important',
            },
            // This Mui-disabled state applies to the TextField root
            '&.Mui-disabled': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.12) !important',
              },
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          },
          "& .MuiInputBase-input": {
            padding: '0 4px',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            fontSize: '14px',
            fontWeight: '500',
            lineHeight: '20px',
            letterSpacing: '0.1px',
            fontFamily: 'Inter',
            // Input text color based on selection or default
            color: selectedLabels.length > 0 ? '#191C1C' : '#3F4949',
            '&::placeholder': {
              color: '#3F4949 !important',
              opacity: 1,
            },
            // Explicitly ensure disabled input text color
            '&.Mui-disabled': {
              WebkitTextFillColor: disabledIconColor + ' !important', // For Webkit browsers
              color: disabledIconColor + ' !important', // For other browsers
            },
          },
          "& .MuiInputLabel-root": {
            transform: 'translate(14px, 7px) scale(1)',
            '&.MuiInputLabel-shrink': {
              transform: 'translate(14px, -9px) scale(0.75)',
              color: '#00696D',
              fontFamily: "Inter",
            },
            color: '#191C1C',
            fontFamily: "Inter",
            fontSize: '14px',
            fontWeight: '500',
            '&.Mui-disabled': {
              color: disabledIconColor + ' !important', // Label color when disabled
            },
          },
        }}
        FormHelperTextProps={{ sx: { margin: 0 } }}
      />

      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        PaperProps={{
          style: {
            borderRadius: "16px 16px 0 0",
            width: "100%",
            maxWidth: "600px",
            position: "fixed",
            bottom: 0,
            margin: 0,
            overflowY: "auto",
            backgroundColor: "#F2F4F4",
            height: '50vh',
          },
        }}
        BackdropProps={{
          onClick: (e) => {
            e.stopPropagation();
            handleClose();
          }
        }}
      >
        <Box sx={{ textAlign: "center", padding: "16px", cursor: "grab" }}>
          <Box sx={{
            width: 32,
            height: 4,
            opacity: 0.4,
            backgroundColor: "#6F7979",
            borderRadius: 2,
            margin: "0 auto",
          }} />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", paddingTop: "8px" }}>
          <IconButton onClick={handleClose} sx={{ color: "#3F4949" }}>
            <CloseIcon />
          </IconButton>
          {subTitle && (
            <Typography
              variant="h6"
              sx={{
                flexGrow: 1,
                marginLeft: '4px',
                fontSize: '22px',
                fontWeight: '400',
                lineHeight: '28px',
                color: '#191C1C',
                fontFamily: 'Inter',
              }}
            >
              {subTitle}
            </Typography>
          )}
        </Box>

        <DialogContent
          ref={dialogContentRef}
          tabIndex={-1}
          sx={{ overflowY: "auto", flexGrow: 1, padding: '0px', "&:focus": { outline: "none" } }}
        >
          <List>
            {options?.length > 0 ? (
              options.map((opt) => (
                <ListItem
                  key={opt.value}
                  button
                  onClick={() => handleToggleOption(opt.value)}
                  disabled={disabled}
                  sx={{
                    paddingTop: '32px',
                    height: 56,
                    px: 2,
                    '&:hover': { backgroundColor: '#f5f5f5' },
                    '&.Mui-disabled': {
                      opacity: 0.5,
                      cursor: 'not-allowed',
                    },
                  }}
                >
                  <ListItemIcon sx={{ paddingLeft: "16px" }}>
                    <Checkbox
                      checked={internalSelectedValue.includes(opt.value)}
                      value={opt.value}
                      edge="start"
                      disabled={disabled}
                      sx={{
                        color: "#6F7979",
                        "&.Mui-checked": {
                          color: "#00696D",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: "1.5rem",
                        },
                        '&.Mui-disabled': {
                          color: 'rgba(0, 0, 0, 0.26) !important',
                        },
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={opt.label}
                    sx={{
                      flexGrow: 1,
                      color: enabledIconAndTextColor, // Apply common color to the list item text
                      fontFamily: 'Inter',
                      fontSize: '16px',
                      fontWeight: '400',
                      lineHeight: '24px',
                      letterSpacing: '0.5px',
                      '&.Mui-disabled': {
                        color: disabledIconColor + ' !important', // List item text color when disabled
                      },
                    }}
                  />
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText
                  primary="No options available"
                  sx={{ textAlign: "center", fontStyle: "italic", color: "#6F7979" }}
                />
              </ListItem>
            )}
          </List>
        </DialogContent>

        <Button
          variant="contained"
          onClick={handleApply}
          disabled={/*internalSelectedValue.length === 0 ||*/ disabled}
          sx={{
            height: "40px",
            backgroundColor: "#00696D",
            color: "#fff",
            borderRadius: "100px",
            margin: "16px auto",
            px: 3,
            textTransform: "none",
            "&:hover": { backgroundColor: "#005B60" },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.26)',
            },
            // Added marginBottom to create padding below the button
            marginBottom: "50px",
          }}
        >
          Bruk
        </Button>
      </Dialog>
    </FormControl>
  );
};

export default MuiMultiSelectListBottomSheet;